import React from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'

import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import {
  Avatar,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  Divider,
  Hidden,
  Typography,
  Zoom
} from '@material-ui/core'

import { UserContext } from '../App'
import { AuthContext } from '../auth'
import ProfileTabs from '../components/profile/ProfileTabs'
import Layout from '../components/shared/Layout'
import LoadingScreen from '../components/shared/LoadingScreen'
import ProfilePicture from '../components/shared/ProfilePicture'
import { FOLLOW_USER, UNFOLLOW_USER } from '../graphql/mutations'
import { GET_USER_PROFILE } from '../graphql/queries'
import { GearIcon } from '../icons'
import { useProfilePageStyles } from '../styles'

function ProfilePage() {
  const { username } = useParams()
  const { currentUserId } = React.useContext(UserContext)
  const classes = useProfilePageStyles()
  const [showOptionsMenu, setOptionsMenu] = React.useState(false)
  const variables = {
    username
  }
  const { data, loading } = useQuery(GET_USER_PROFILE, {
    variables,
    fetchPolicy: 'no-cache'
  })

  if (loading) return <LoadingScreen />
  const [user] = data.users
  const isOwner = currentUserId === user.id

  function handleOptionsMenuClick() {
    setOptionsMenu(true)
  }

  function handelCloseMenu() {
    setOptionsMenu(false)
  }

  return (
    <Layout title={`${user.name} (@${user.username})`}>
      <div className={classes.container}>
        <Hidden xsDown>
          <Card className={classes.cardLarge}>
            <ProfilePicture isOwner={isOwner} image={user.profile_image} />
            <CardContent className={classes.cardContentLarge}>
              <ProfileNameSection
                user={user}
                isOwner={isOwner}
                handleOptionsMenuClick={handleOptionsMenuClick}
              />
              <PostCountSection user={user} />
              <NameBioSection user={user} />
            </CardContent>
          </Card>
        </Hidden>
        <Hidden smUp>
          <Card className={classes.cardSmall}>
            <CardContent>
              <section className={classes.sectionSmall}>
                <ProfilePicture
                  size={77}
                  isOwner={isOwner}
                  image={user.profile_image}
                />
                <ProfileNameSection
                  user={user}
                  isOwner={isOwner}
                  handleOptionsMenuClick={handleOptionsMenuClick}
                />
              </section>
              <NameBioSection user={user} />
            </CardContent>
            <PostCountSection user={user} />
          </Card>
        </Hidden>
        {showOptionsMenu && <OptionsMenu handelCloseMenu={handelCloseMenu} />}
        <ProfileTabs user={user} isOwner={isOwner} />
      </div>
    </Layout>
  )
}

function ProfileNameSection({ user, isOwner, handleOptionsMenuClick }) {
  const classes = useProfilePageStyles()
  const [showUnfollowDialog, setUnfollowDialog] = React.useState(false)
  const { currentUserId, followingIds, followerIds } = React.useContext(
    UserContext
  )
  const isAlreadyFollowing = followingIds.some(id => id === user.id)
  const [isFollowing, setFollowing] = React.useState(isAlreadyFollowing)
  const isFollower = !isFollowing && followerIds.some(id => id === user.id)
  const variables = {
    userIdToFollow: user.id,
    currentUserId
  }
  const [followUser] = useMutation(FOLLOW_USER)

  function handleFollowUser() {
    setFollowing(true)
    followUser({ variables })
  }

  const onUnfollowUser = React.useCallback(() => {
    setUnfollowDialog(false)
    setFollowing(false)
  }, [])

  let followButton
  if (isFollowing) {
    followButton = (
      <Button
        onClick={() => setUnfollowDialog(true)}
        variant="outlined"
        className={classes.button}
      >
        Following
      </Button>
    )
  } else if (isFollower) {
    followButton = (
      <Button
        onClick={handleFollowUser}
        variant="contained"
        color="primary"
        className={classes.button}
      >
        Follow Back
      </Button>
    )
  } else {
    followButton = (
      <Button
        onClick={handleFollowUser}
        variant="contained"
        color="primary"
        className={classes.button}
      >
        Follow
      </Button>
    )
  }

  return (
    <>
      <Hidden xsDown>
        <section className={classes.usernameSection}>
          <Typography>{user.username}</Typography>
          {isOwner ? (
            <>
              <Link to="/accounts/edit">
                <Button variant="outlined">Edit Profile</Button>
              </Link>
              <div
                onClick={handleOptionsMenuClick}
                className={classes.settingsWrapper}
              >
                <GearIcon className={classes.settings} />
              </div>
            </>
          ) : (
            <>{followButton}</>
          )}
        </section>
      </Hidden>
      <Hidden smUp>
        <section>
          <div className={classes.usernameDivSmall}>
            <Typography>{user.username}</Typography>
            {isOwner && (
              <div
                onClick={handleOptionsMenuClick}
                className={classes.settingsWrapper}
              >
                <GearIcon className={classes.settings} />
              </div>
            )}
          </div>
          {isOwner ? (
            <Link to="/accounts/edit">
              <Button variant="outlined" style={{ width: '100%' }}>
                Edit Profile
              </Button>
            </Link>
          ) : (
            followButton
          )}
        </section>
      </Hidden>
      {showUnfollowDialog && (
        <UnfollowDialog
          onUnfollowUser={onUnfollowUser}
          onClose={() => setUnfollowDialog(false)}
          user={user}
        />
      )}
    </>
  )
}

function UnfollowDialog({ user, onClose, onUnfollowUser }) {
  const classes = useProfilePageStyles()
  const { currentUserId } = React.createContext(UserContext)
  const [unfollowUser] = useMutation(UNFOLLOW_USER)

  function handleUnfollowUser() {
    const variables = {
      userIdToFollow: user.id,
      currentUserId
    }
    unfollowUser({ variables })
    onUnfollowUser()
  }

  return (
    <Dialog
      open
      classes={{
        scrollPaper: classes.unfollowDialogScrollPaper
      }}
      onClose={onClose}
      TransitionComponent={Zoom}
    >
      <div className={classes.wrapper}>
        <Avatar
          src={user.profile_image}
          alt={`${user.username}'s avatar`}
          className={classes.avatar}
        />
      </div>
      <Typography
        align="center"
        variant="body2"
        className={classes.unfollowDialogText}
      >
        Unfollow @{user.username}
      </Typography>
      <Divider />
      <Button onClick={handleUnfollowUser} className={classes.unfollowButton}>
        Unfollow
      </Button>
      <Divider />
      <Button onClick={onClose} className={classes.cancelButton}>
        Cancel
      </Button>
    </Dialog>
  )
}

function OptionsMenu({ handelCloseMenu }) {
  const classes = useProfilePageStyles()
  const [showLogOutMessage, setLogOutMessage] = React.useState(false)
  const { signOut } = React.useContext(AuthContext)
  const history = useHistory()
  const client = useApolloClient()

  function handleLogOutClick() {
    setLogOutMessage(true)
    setTimeout(async () => {
      await client.clearStore()
      signOut()
      history.push('accounts/login')
    }, 2000)
  }

  return (
    <Dialog
      open
      classes={{
        scrollPaper: classes.dialogScrollPaper,
        paper: classes.dialogPaper
      }}
      TransitionComponent={Zoom}
    >
      {showLogOutMessage ? (
        <DialogTitle className={classes.dialogTitle}>
          Logging Out
          <Typography color="textSecondary">
            You need to log back in to continue using Instagram.
          </Typography>
        </DialogTitle>
      ) : (
        <>
          <OptionsItem text="Change Password" />
          <OptionsItem text="Nametag" />
          <OptionsItem text="Authorized Apps" />
          <OptionsItem text="Notifications" />
          <OptionsItem text="Privacy & Security" />
          <OptionsItem text="Log Out" onClick={handleLogOutClick} />
          <OptionsItem text="Cancel" onClick={handelCloseMenu} />
        </>
      )}
    </Dialog>
  )
}

function PostCountSection({ user }) {
  const classes = useProfilePageStyles()
  const options = ['posts', 'followers', 'following']

  return (
    <>
      <Hidden smUp>
        <Divider />
      </Hidden>
      <section className={classes.followingSection}>
        {options.map(option => (
          <div key={option} className={classes.followingText}>
            <Typography className={classes.followingCount}>
              {user[`${option}_aggregate`].aggregate.count}
            </Typography>
            <Hidden xsDown>
              <Typography>{option}</Typography>
            </Hidden>
            <Hidden smUp>
              <Typography color="textSecondary">{option}</Typography>
            </Hidden>
          </div>
        ))}
      </section>
      <Hidden smUp>
        <Divider />
      </Hidden>
    </>
  )
}

function NameBioSection({ user }) {
  const classes = useProfilePageStyles()

  return (
    <section className={classes.section}>
      <Typography className={classes.typography}>{user.name}</Typography>
      <Typography>{user.bio}</Typography>
      <a href={user.website} target="_blank" rel="noopener noreferrer">
        <Typography
          color="secondary"
          className={classes.typography}
          component="span"
        >
          {user.website}
        </Typography>
      </a>
    </section>
  )
}

function OptionsItem({ text, onClick }) {
  return (
    <>
      <Button style={{ padding: '12px 8px' }} onClick={onClick}>
        {text}
      </Button>
      <Divider />
    </>
  )
}

export default ProfilePage
